export interface StoredWebWidgetSession {
  token: string;
  sessionId: string;
  expiresAtUtc: string;
}

export interface StoredWebWidgetIntro {
  introCompleted: boolean;
  displayName?: string;
  externalVisitorId?: string;
  email?: string;
  phone?: string;
}

const SESSION_STORAGE_PREFIX = 'sa_wc_session';
const INTRO_STORAGE_PREFIX = 'sa_wc_intro';

const normalizeKeyPart = (value: string): string => value.trim().toLowerCase();

const buildScopedKey = (prefix: string, tenantCode: string, widgetKey: string): string => {
  return `${prefix}:${normalizeKeyPart(tenantCode)}:${normalizeKeyPart(widgetKey)}`;
};

const canUseStorage = (storage: Storage | undefined): storage is Storage => {
  if (!storage) {
    return false;
  }

  try {
    const probeKey = '__sa_wc_storage_probe__';
    storage.setItem(probeKey, '1');
    storage.removeItem(probeKey);
    return true;
  } catch {
    return false;
  }
};

const getLocalStorage = (): Storage | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return canUseStorage(window.localStorage) ? window.localStorage : undefined;
};

const getSessionStorage = (): Storage | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return canUseStorage(window.sessionStorage) ? window.sessionStorage : undefined;
};

const isStoredSessionActive = (session: StoredWebWidgetSession): boolean => {
  const expiresAt = Date.parse(session.expiresAtUtc);
  return Number.isFinite(expiresAt) && expiresAt > Date.now();
};

export const readStoredWebWidgetSession = (
  tenantCode: string,
  widgetKey: string,
): StoredWebWidgetSession | null => {
  const storage = getLocalStorage();
  if (!storage) {
    return null;
  }

  try {
    const raw = storage.getItem(buildScopedKey(SESSION_STORAGE_PREFIX, tenantCode, widgetKey));
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<StoredWebWidgetSession>;
    const token = typeof parsed.token === 'string' ? parsed.token.trim() : '';
    const sessionId = typeof parsed.sessionId === 'string' ? parsed.sessionId.trim() : '';
    const expiresAtUtc = typeof parsed.expiresAtUtc === 'string' ? parsed.expiresAtUtc.trim() : '';

    if (!token || !sessionId || !expiresAtUtc) {
      return null;
    }

    const session = { token, sessionId, expiresAtUtc };
    if (!isStoredSessionActive(session)) {
      storage.removeItem(buildScopedKey(SESSION_STORAGE_PREFIX, tenantCode, widgetKey));
      return null;
    }

    return session;
  } catch {
    return null;
  }
};

export const writeStoredWebWidgetSession = (
  tenantCode: string,
  widgetKey: string,
  session: StoredWebWidgetSession,
): void => {
  const storage = getLocalStorage();
  if (!storage) {
    return;
  }

  try {
    storage.setItem(
      buildScopedKey(SESSION_STORAGE_PREFIX, tenantCode, widgetKey),
      JSON.stringify(session),
    );
  } catch {
    // Ignore quota or privacy mode failures.
  }
};

export const clearStoredWebWidgetSession = (tenantCode: string, widgetKey: string): void => {
  const storage = getLocalStorage();
  if (!storage) {
    return;
  }

  try {
    storage.removeItem(buildScopedKey(SESSION_STORAGE_PREFIX, tenantCode, widgetKey));
  } catch {
    // Ignore.
  }
};

export const readStoredWebWidgetIntro = (
  tenantCode: string,
  widgetKey: string,
): StoredWebWidgetIntro | null => {
  const storage = getSessionStorage();
  if (!storage) {
    return null;
  }

  try {
    const raw = storage.getItem(buildScopedKey(INTRO_STORAGE_PREFIX, tenantCode, widgetKey));
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<StoredWebWidgetIntro>;
    return {
      introCompleted: Boolean(parsed.introCompleted),
      displayName: typeof parsed.displayName === 'string' ? parsed.displayName : undefined,
      externalVisitorId: typeof parsed.externalVisitorId === 'string' ? parsed.externalVisitorId : undefined,
      email: typeof parsed.email === 'string' ? parsed.email : undefined,
      phone: typeof parsed.phone === 'string' ? parsed.phone : undefined,
    };
  } catch {
    return null;
  }
};

export const writeStoredWebWidgetIntro = (
  tenantCode: string,
  widgetKey: string,
  intro: StoredWebWidgetIntro,
): void => {
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }

  try {
    storage.setItem(
      buildScopedKey(INTRO_STORAGE_PREFIX, tenantCode, widgetKey),
      JSON.stringify(intro),
    );
  } catch {
    // Ignore.
  }
};

export const clearStoredWebWidgetIntro = (tenantCode: string, widgetKey: string): void => {
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }

  try {
    storage.removeItem(buildScopedKey(INTRO_STORAGE_PREFIX, tenantCode, widgetKey));
  } catch {
    // Ignore.
  }
};
